import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { List } from 'lucide-react'
import { useCompetitorWatchlist } from './use-competitor-watchlist'
import { useCompetitorWatchlistActions } from './use-competitor-watchlist-actions'
import { EmptyState } from './ui/empty-state'
import { LoadingState } from './ui/loading-state'
import { AccountsList } from './ui/accounts-list.tsx'
import { TopPostsList } from './ui/top-posts-list.tsx'
import { Separator } from '@/components/ui/separator'
import { ActionsMenu } from './ui/actions-menu'

export function CompetitorWatchlistSection() {
  const navigate = useNavigate()
  const { loading, hasWatchlist, watchlistId, watchlist, topPosts, refetch } = useCompetitorWatchlist()
  const {
    refreshingId,
    deletingId,
    deleteDialog,
    openDeleteDialog,
    closeDeleteDialog,
    handleDeleteWatchlist,
    handleRefreshWatchlist,
  } = useCompetitorWatchlistActions({
    watchlistId: watchlistId || '',
    watchlistName: watchlist?.name || 'Competitor Watchlist',
    refetch,
    onNewWatchlistId: (id) => {
      setTimeout(() => navigate(`/instagram/watchlist/${id}`), 1000)
    }
  })

  const handleWatchlistCreated = (_newWatchlistId: string) => {
    // Trigger a refetch to update the UI with the new watchlist
    setTimeout(() => refetch(), 1000)
  }

  // Action handlers moved into useCompetitorWatchlistActions

  if (loading) return <LoadingState />
  if (!hasWatchlist || !watchlist) return <EmptyState 
    onCreate={() => navigate('/create-watchlist')} 
    onWatchlistCreated={handleWatchlistCreated}
  />

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <List className="h-5 w-5" />
              Competitor Watchlist
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/instagram/watchlist/${watchlistId}`)}>
                View Full Watchlist
              </Button>
              <ActionsMenu
                refreshing={refreshingId === watchlistId}
                onRefresh={() => handleRefreshWatchlist()}
                onDelete={() => openDeleteDialog()}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <AccountsList accounts={watchlist.accounts} />
            <TopPostsList topPosts={topPosts} />
          </div>
          <Separator className="my-4" />
          <div className="text-xs text-muted-foreground">Showing {watchlist.total_accounts} accounts</div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.open}
        onOpenChange={closeDeleteDialog}
        title="Delete Competitor Watchlist"
        description={`Are you sure you want to delete "${deleteDialog.watchlistName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="destructive"
        onConfirm={handleDeleteWatchlist}
        loading={deletingId === deleteDialog.watchlistId}
      />
    </>
  )
}


